'use client';

import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import type { AppRole } from '@/types/roles';
import { ROLE_CONFIGS } from '@/types/roles';

interface RoleGuardProps {
  /** The role required to view this content */
  requiredRole: AppRole;
  children: ReactNode;
  /** Optional: show a fallback instead of redirect */
  fallback?: ReactNode;
}

/**
 * RoleGuard — wraps app-specific pages to ensure only the correct role can access them.
 *
 * CRITICAL RULES to prevent infinite loops:
 * 1. NEVER redirect while auth state is still loading (loading=true or profileLoading=true)
 * 2. Wait until BOTH loading AND profileLoading are false before making any redirect decision
 * 3. If auth check takes too long (>10s), show login instead of spinning forever
 * 4. Do NOT cross-redirect between portals — show access-denied or login instead
 * 5. If role cannot be resolved due to DB failure, show retryable error — never treat as customer
 */
export function RoleGuard({ requiredRole, children, fallback }: RoleGuardProps) {
  const { isAuthenticated, userRole, loading, profileLoading, sessionExpired, refreshSession } = useAuth();
  const router = useRouter();
  const [timedOut, setTimedOut] = useState(false);
  const [dbError, setDbError] = useState(false);
  const redirectedRef = useRef(false);

  const loginRoute = ROLE_CONFIGS[requiredRole]?.loginRoute || '/apps/admin/login';
  const appType = process.env.NEXT_PUBLIC_APP_TYPE;

  // Dev-mode diagnostics
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.info('[RoleGuard] State:', {
        requiredRole,
        resolvedRole: userRole,
        NEXT_PUBLIC_APP_TYPE: appType,
        authLoading: loading,
        profileLoading,
        isAuthenticated,
        sessionExpired,
      });
    }
  }, [requiredRole, userRole, appType, loading, profileLoading, isAuthenticated, sessionExpired]);

  // Safety timeout — if auth check takes more than 10 seconds, stop spinning and show login
  // Keep the latest loading flags in a ref: the timer below is created once on mount,
  // so reading `loading` directly would use the stale first-render value (always true).
  const loadingRef = useRef(loading);
  const profileLoadingRef = useRef(profileLoading);
  useEffect(() => {
    loadingRef.current = loading;
    profileLoadingRef.current = profileLoading;
  }, [loading, profileLoading]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (loadingRef.current || profileLoadingRef.current) {
        console.warn(`[RoleGuard] Auth check timed out after 10s for role=${requiredRole} — showing login`);
        setTimedOut(true);
      }
    }, 10000);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset timeout flag when loading completes
  useEffect(() => {
    if (!loading && !profileLoading) {
      setTimedOut(false);
    }
  }, [loading, profileLoading]);

  // Handle session expiry
  useEffect(() => {
    if (sessionExpired) {
      console.warn(`[RoleGuard] Session expired — redirecting to ${loginRoute}`);
      redirectedRef.current = true;
      router.replace(`${loginRoute}?reason=session_expired`);
    }
  }, [sessionExpired, loginRoute, router]);

  useEffect(() => {
    // CRITICAL: Do NOT redirect while auth state is still loading
    if (loading || profileLoading) {
      console.info(`[RoleGuard] Waiting for auth: loading=${loading} profileLoading=${profileLoading}`);
      return;
    }

    // Prevent duplicate redirects in the same render cycle
    if (redirectedRef.current) return;

    console.info(`[RoleGuard] Auth resolved: isAuthenticated=${isAuthenticated} userRole=${userRole} requiredRole=${requiredRole}`);

    // Not authenticated → go to the correct login page (ONCE)
    if (!isAuthenticated) {
      console.info(`[RoleGuard] Not authenticated → redirecting to ${loginRoute}`);
      redirectedRef.current = true;
      router.replace(loginRoute);
      return;
    }

    // Authenticated but role is null — could be a DB failure, not a role mismatch
    // Show a retryable error instead of treating as wrong role
    if (userRole === null) {
      console.warn('[RoleGuard] Role could not be resolved — possible DB/network failure');
      setDbError(true);
      return;
    }

    // Clear any previous DB error if role resolved
    setDbError(false);

    // Authenticated but wrong role
    if (userRole !== requiredRole) {
      console.info(`[RoleGuard] Wrong role: has=${userRole} needs=${requiredRole} appType=${appType}`);

      // On subdomain deployments: do NOT cross-redirect to other subdomain
      // Show access-denied instead to avoid cross-portal loops
      if (appType === 'admin' || appType === 'vendor') {
        console.info(`[RoleGuard] Subdomain mismatch — redirecting to access-denied`);
        redirectedRef.current = true;
        router.replace('/access-denied');
        return;
      }

      // Monorepo / dev: redirect to correct app route
      const correctRoute = ROLE_CONFIGS[userRole]?.defaultRoute || '/';
      console.info(`[RoleGuard] Redirecting to correct portal: ${correctRoute}`);
      redirectedRef.current = true;
      router.replace(correctRoute);
    }
  }, [loading, profileLoading, isAuthenticated, userRole, requiredRole, loginRoute, router, appType]);

  // Reset redirect guard when auth state changes (e.g., user signs in)
  useEffect(() => {
    redirectedRef.current = false;
    setDbError(false);
  }, [isAuthenticated, userRole]);

  // Show timeout fallback — redirect to login
  if (timedOut) {
    return (
      fallback || (
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center max-w-sm px-4">
            <div className="w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3a9 9 0 100 18A9 9 0 0012 3z" />
              </svg>
            </div>
            <p className="text-sm font-medium text-foreground mb-1">Session verification timed out</p>
            <p className="text-xs text-muted-foreground mb-4">Please sign in again to continue.</p>
            <a
              href={loginRoute}
              className="inline-block px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors"
            >
              Go to Login
            </a>
          </div>
        </div>
      )
    );
  }

  // DB/network error — role could not be resolved
  if (dbError && !loading && !profileLoading && isAuthenticated) {
    return (
      fallback || (
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center max-w-sm px-4">
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3a9 9 0 100 18A9 9 0 0012 3z" />
              </svg>
            </div>
            <p className="text-sm font-medium text-foreground mb-1">Could not verify your role</p>
            <p className="text-xs text-muted-foreground mb-4">
              There was a problem loading your account details. This is usually temporary.
            </p>
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => { setDbError(false); window.location.reload(); }}
                className="px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors"
              >
                Retry
              </button>
              <a
                href={loginRoute}
                className="px-4 py-2 bg-muted text-foreground text-sm font-medium rounded-lg hover:bg-muted/80 transition-colors"
              >
                Sign In Again
              </a>
            </div>
          </div>
        </div>
      )
    );
  }

  // Show spinner while loading — NEVER redirect during this phase
  if (loading || profileLoading) {
    return (
      fallback || (
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Verifying session...</p>
          </div>
        </div>
      )
    );
  }

  // Not authenticated — show spinner while redirect is in progress
  if (!isAuthenticated) {
    return (
      fallback || (
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Redirecting to login...</p>
          </div>
        </div>
      )
    );
  }

  // Wrong role — show spinner while redirect is in progress
  if (userRole && userRole !== requiredRole) {
    return (
      fallback || (
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Redirecting...</p>
          </div>
        </div>
      )
    );
  }

  return <>{children}</>;
}

export default RoleGuard;
